import { Box, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Segment } from '@feathr/blackbox';
import { Button, Modal, Tab, Tabs } from '@feathr/components';
import { useFlags } from '@feathr/extender/state';

import { useGroupExportModal } from './GroupExportModal.hooks';
import PersonTab from './PersonTab';

import * as styles from './GroupExportModal.css';

export interface IGroupExportModalProps {
  /** Whether the modal is visible. Parent should conditionally render so the hook only mounts when open. */
  opened: boolean;
  onClose: () => void;
  segment: Segment;
}

function GroupExportModal({
  opened,
  onClose,
  segment,
}: Readonly<IGroupExportModalProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const { showEmailActivityExport } = useFlags();
  const state = useGroupExportModal({ segment, onClose });
  const { activeTab, setActiveTab, isLoading, isSubmitDisabled, handleSubmitPerson, handleClose } =
    state;

  // Block dismissal (Cancel, overlay click, Esc) while a submit is in flight so
  // the toast never lands on an already-closed modal.
  const handleGuardedClose = useCallback((): void => {
    if (isLoading) {
      return;
    }
    handleClose();
  }, [handleClose, isLoading]);

  function handleSelectPersonTab(): void {
    setActiveTab('person');
  }

  function handleSelectEmailActivityTab(): void {
    setActiveTab('emailActivity');
  }

  function handleSelectOtherActivitiesTab(): void {
    setActiveTab('otherActivities');
  }

  return (
    <Modal
      allowOverflow
      bodyClassName={styles.body}
      description={t(
        'Export the data in the chosen group as a .csv file. When your export file is ready, we will send you an email with a download link.',
      )}
      onClose={handleGuardedClose}
      opened={opened}
      rightActions={[
        <Button disabled={isLoading} key={'cancel'} onClick={handleGuardedClose}>
          {t('Cancel')}
        </Button>,
        <Button
          disabled={activeTab !== 'person' || isSubmitDisabled}
          isLoading={isLoading}
          key={'confirm'}
          onClick={handleSubmitPerson}
          type={'primary'}
        >
          {t('Export')}
        </Button>,
      ]}
      title={t('Export Group')}
    >
      {showEmailActivityExport ? (
        <>
          <Tabs className={styles.tabs}>
            <Tab
              active={activeTab === 'person'}
              onClick={handleSelectPersonTab}
              title={t('Person')}
            />
            <Tab
              active={activeTab === 'emailActivity'}
              onClick={handleSelectEmailActivityTab}
              title={t('Email Activity')}
            />
            <Tab
              active={activeTab === 'otherActivities'}
              onClick={handleSelectOtherActivitiesTab}
              title={t('Other Activities')}
            />
          </Tabs>
          {activeTab === 'person' && <PersonTab state={state} />}
          {activeTab === 'emailActivity' && (
            <Box className={styles.placeholder} data-name={'email-activity-tab-placeholder'} />
          )}
          {activeTab === 'otherActivities' && (
            <Box className={styles.placeholder} data-name={'other-activities-tab-placeholder'} />
          )}
        </>
      ) : (
        <Box mt={theme.spacing['3']}>
          <PersonTab state={state} />
        </Box>
      )}
    </Modal>
  );
}

export default observer(GroupExportModal);
